import maplibre, {
  AddLayerObject,
  GeoJSONSource,
  LngLat,
  LngLatLike,
  MapGeoJSONFeature,
  MapMouseEvent,
  Marker,
  Map as MlMap,
  ResourceType,
} from 'maplibre-gl';
import {
  isMapboxURL,
  transformMapboxUrl,
} from 'maplibregl-mapbox-request-transformer';
import * as turf from '@turf/turf';
import { debounce } from 'ts-debounce';
import { GeoJSON } from 'geojson';
import {
  FeatureCollection,
  Polygon,
  LineString,
  MultiLineString,
} from 'geojson';
import { GeoJSONType } from '@/types/geojson';
import { Burst, BurstStatus, ScoutLocation } from '@/types/location';
import { EventTypes, InitializationParams, SourceOptions } from '@/types/map';
import { FiltersState } from '@/types/scout';
import { getMapboxAccessToken } from '@/utils/map';
import { colors, hslStringToHex } from '@/utils/styles';
import { throttleDebounce } from '@/utils/helpers';
import { buildUrlParamString } from './url';
import { isValidSetOfCoordinates } from './validation';

const NEW_LOCATION_SOURCE = 'hm-new-location-source';
const NEW_LOCATION_LAYER = 'hm-new-location-layer';

export const transformRequest = (url: string, resourceType?: ResourceType) => {
  if (!url || !resourceType) return;

  if (isMapboxURL(url)) {
    return transformMapboxUrl(url, resourceType, getMapboxAccessToken());
  }

  return { url };
};

const triggerEvent = (
  e: MapMouseEvent & {
    features?: MapGeoJSONFeature[] | undefined;
  },
  eventKey: EventTypes,
  map: MlMap,
  clickCallback?: (id: string | number) => void,
) => {
  const { id } = e.features?.[0] || {};

  switch (eventKey) {
    case EventTypes.CLICK:
      if (!id) return;
      clickCallback && clickCallback(id);
      break;
    case EventTypes.MOUSE_ENTER:
      map.getCanvas().style.cursor = 'pointer';
      break;
    case EventTypes.MOUSE_LEAVE:
      map.getCanvas().style.cursor = '';
      break;
    default:
      break;
  }
};

const boundEventsMap = new Set<string>();
export const handleBoundEvents = (
  map: MlMap,
  layers: string[],
  clickCallback?: (id: string | number) => void,
) => {
  Object.values(EventTypes).forEach(eventType => {
    layers.forEach(layer => {
      const eventKey = `${layer}-${eventType}`;

      if (!boundEventsMap.has(eventKey)) {
        map.on(eventType, layer, e => {
          triggerEvent(e, eventType, map, clickCallback);
        });

        boundEventsMap.add(eventKey);
      }
    });
  });
};

export const clearStaticLayersAndSources = (
  map: MlMap,
  layerIds: string[],
  sourceIds: string[],
) => {
  map.getStyle().layers.forEach(layer => {
    layerIds.forEach(layerId => {
      if (layer.id.includes(layerId)) {
        map.removeLayer(layer.id);
      }
    });
  });

  Object.keys(map.getStyle().sources).forEach(source => {
    sourceIds.forEach(sourceId => {
      if (source.includes(sourceId)) {
        map.removeSource(source);
      }
    });
  });
};

export const clearAllDynamicLayersAndSources = (
  map: MlMap,
  keyword: 'dynamic' | 'active',
) => {
  map.getStyle().layers.forEach(layer => {
    if (layer.id.includes(keyword)) {
      map.removeLayer(layer.id);
    }
  });

  Object.keys(map.getStyle().sources).forEach(source => {
    if (source.includes(keyword)) {
      map.removeSource(source);
    }
  });
};

export const createLayersAndSources = (
  map: MlMap,
  layerOptions: AddLayerObject[],
  sourceOptions: SourceOptions[],
) => {
  sourceOptions.forEach(sourceOption => {
    map.addSource(sourceOption.id, {
      type: sourceOption.options.type,
      data: sourceOption.options.data,
      promoteId: sourceOption.options.promoteId ? 'id' : undefined,
    });
  });

  layerOptions.forEach(layerOption => {
    map.addLayer(layerOption);
  });
};

export const initializeMap = ({
  mapContainer,
  onLoadCallback,
  zoom,
  center,
  bounds,
  options,
  style,
}: InitializationParams) => {
  const newMap = new maplibre.Map({
    container: mapContainer,
    style: style || 'mapbox://styles/arielseidman/clyxl0l2b007m01r568cphb2k',
    center,
    zoom,
    transformRequest,
    maxPitch: 0,
    attributionControl: false,
    bounds,
    fitBoundsOptions: options,
  });

  const nav = new maplibre.NavigationControl({
    showCompass: true,
  });
  newMap.addControl(nav, 'bottom-right');

  newMap.on('load', function () {
    onLoadCallback(newMap);
  });

  newMap.on('error', (e: any) => {
    if (e && !e.error.message.includes('AJAXError')) {
      console.warn('Map error: ', e);
    }
  });

  return newMap;
};

export const getBounds = (
  coords: [number, number][],
): [LngLatLike, LngLatLike] => {
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;

  for (const [lng, lat] of coords) {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  }

  return [
    { lon: minLng, lat: minLat },
    { lon: maxLng, lat: maxLat },
  ];
};

export const capitalizeFirstCharacter = (str: string) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const camelCaseToTitle = (str: string) => {
  const spaced = str.replace(/([A-Z])/g, ' $1');
  const capitalized = spaced.charAt(0).toUpperCase() + spaced.slice(1);
  return capitalized;
};

export const buildErrorMessage = (failures: Record<string, number>) => {
  let totalFailures = 0;
  const filteredFailures = Object.entries(failures).filter(([key, value]) => {
    totalFailures += value;
    return value > 0;
  });
  const errorMessage = `${totalFailures} location${totalFailures > 1 ? 's' : ''} failed to get added.`;
  let reasons = 'Reason: ';
  const last = filteredFailures.length - 1;
  let index = 0;
  filteredFailures.forEach(([key, value]) => {
    const formattedKey = camelCaseToTitle(key);
    reasons += `${formattedKey}${index === last ? '' : ', '}`;
    index++;
  });

  return { errorMessage, reasons };
};

export const standardizeType = (type: string | null) => {
  if (!type) return '';

  if (type.toLowerCase() === GeoJSONType.Address.toLowerCase()) {
    return GeoJSONType.Address;
  }

  if (type.toLowerCase() === GeoJSONType.Point.toLowerCase()) {
    return GeoJSONType.Point;
  }

  if (type.toLowerCase() === GeoJSONType.Polygon.toLowerCase()) {
    return GeoJSONType.Polygon;
  }

  if (type.toLowerCase() === GeoJSONType.MultiPolygon.toLowerCase()) {
    return GeoJSONType.MultiPolygon;
  }

  return type;
};

export const filterLocations = (
  locations: ScoutLocation[],
  filters: FiltersState,
) => {
  return locations.filter(location => {
    if (filters.search) {
      const hasSearch =
        location.description?.toLowerCase().includes(filters.search) ||
        location.name?.toLowerCase().includes(filters.search);
      if (!hasSearch) {
        return false;
      }
    }

    return true;
  });
};

export const createLocationFromMap = (map: MlMap, lat: number, lon: number) => {
  const marker = new maplibre.Marker({
    draggable: true,
  })
    .setLngLat({ lat, lon })
    .addTo(map);

  const geojson = convertPointToPolygon(`${lon}, ${lat}`);
  if (geojson) {
    const featureCollection: GeoJSON = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: geojson,
          properties: {
            id: 'new-location',
          },
        },
      ],
    };

    clearStaticLayersAndSources(
      map,
      [NEW_LOCATION_LAYER],
      [NEW_LOCATION_SOURCE],
    );

    const sourceOptions: SourceOptions[] = [
      {
        id: NEW_LOCATION_SOURCE,
        options: {
          type: 'geojson',
          data: featureCollection,
          promoteId: 'id',
        },
      },
    ];

    const mapLayersOptions: AddLayerObject[] = [
      {
        id: NEW_LOCATION_LAYER,
        type: 'fill',
        source: NEW_LOCATION_SOURCE,
        minzoom: 0,
        layout: {},
        paint: {
          'fill-color': hslStringToHex(colors.scoutBoundary.main),
          'fill-opacity': 0.5,
        },
      },
    ];

    createLayersAndSources(map, mapLayersOptions, sourceOptions);
  }

  const onDragChange = (center: LngLat) => {
    const geojson = convertPointToPolygon(
      `${center.lng}, ${center.lat}`,
    ) as Polygon;

    const featureCollection: GeoJSON = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: geojson,
          properties: {},
        },
      ],
    };

    const source = map.getSource(NEW_LOCATION_SOURCE);
    if (source && source.type === 'geojson') {
      (source as GeoJSONSource).setData(featureCollection);
    }
  };

  const debounceOnDragChange = throttleDebounce(onDragChange, 150, 150);

  marker.on('drag', () => {
    const center = marker.getLngLat();

    if (center) {
      debounceOnDragChange(center);
    }
  });

  return marker;
};

export const removeLocationFromMap = (map: MlMap, marker: Marker | null) => {
  if (marker) {
    marker.remove();

    clearStaticLayersAndSources(
      map,
      [NEW_LOCATION_LAYER],
      [NEW_LOCATION_SOURCE],
    );
  }
};

export const getLatestBurst = (bursts?: Burst[]): Burst | undefined => {
  if (!bursts) return undefined;

  const latest = bursts[bursts.length - 1];
  return latest;
};

export const isLatestBurstActive = (bursts?: Burst[]): boolean => {
  const latest = getLatestBurst(bursts);
  return latest?.status === BurstStatus.ACTIVE;
};

export const appendUrlParams = debounce(params => {
  const urlQuery = buildUrlParamString(params);
  window.history.pushState(null, '', urlQuery);
}, 500);

export const combineLinesToMultiLineString = (
  lines: LineString[] | MultiLineString[],
): MultiLineString => {
  const f = lines.map(line => turf.feature(line));
  const fc = turf.featureCollection(f);
  const mergedFc = turf.combine(fc as FeatureCollection<LineString>);
  const multiLineString = mergedFc.features[0].geometry as MultiLineString;

  return multiLineString;
};

export const convertPointToPolygon = (
  coordinates: string,
  reverse: boolean = false,
) => {
  const coordsArray = coordinates.split(',').map(Number);

  if (reverse) {
    coordsArray.reverse();
  }

  const { valid, errors } = isValidSetOfCoordinates(coordsArray);

  if (!valid) {
    console.error(`Invalid coordinates: `, errors);
    return null;
  }

  const center = turf.point([coordsArray[0], coordsArray[1]]);

  const radius = 20; // 20 meters

  const circle = turf.circle(center, radius, {
    steps: 64, // number of steps in circular polygon
    units: 'meters',
  });

  return circle.geometry;
};
